import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';

interface StudentPromotionProps {
  domain: string;
}

interface Student {
  id: string;
  studentId: string;
  name: string;
  class: string;
  section: string;
  rollNumber: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  [key: string]: any; // Allow other fields
}

interface PromotionRecord {
  studentId: string;
  name: string;
  currentClass: string;
  currentSection: string;
  newClass: string;
  newSection: string;
  status: 'pending' | 'success' | 'failed';
  error?: string;
}

const StudentPromotion: React.FC<StudentPromotionProps> = ({ domain }) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [currentSectionFilter, setCurrentSectionFilter] = useState('all');
  const [targetSection, setTargetSection] = useState('');
  const [studentSections, setStudentSections] = useState<Record<string, string>>({});
  const [promotionRecords, setPromotionRecords] = useState<PromotionRecord[]>([]);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<'select' | 'review' | 'results'>('select');
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());

  const classes = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  const sections = ['A', 'B', 'C', 'D', 'E', 'F'];

  useEffect(() => {
    if (selectedClass) {
      loadStudents();
    }
  }, [selectedClass, currentSectionFilter, domain]);

  // Update all student sections when targetSection changes
  useEffect(() => {
    if (targetSection && students.length > 0) {
      const updatedSections: Record<string, string> = {};
      students.forEach(student => {
        updatedSections[student.id] = targetSection;
      });
      setStudentSections(updatedSections);
    }
  }, [targetSection]);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const studentsRef = collection(db, `school-users/${domain}/students`);
      const snapshot = await getDocs(studentsRef);

      const studentsList: Student[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        // Filter by selected class and section
        const matchesClass = data.class === selectedClass;
        const matchesSection = currentSectionFilter === 'all' || data.section === currentSectionFilter;

        if (matchesClass && matchesSection) {
          studentsList.push({
            id: docSnap.id,
            ...data,
          } as Student);
        }
      });

      const sortedStudents = studentsList.sort((a, b) => {
        // Sort by section then roll number
        if (a.section !== b.section) {
          return a.section.localeCompare(b.section);
        }
        return a.rollNumber.localeCompare(b.rollNumber);
      });

      setStudents(sortedStudents);

      // Initialize student sections based on targetSection or their current section
      const initialSections: Record<string, string> = {};
      sortedStudents.forEach(student => {
        initialSections[student.id] = targetSection || student.section;
      });
      setStudentSections(initialSections);

      // Select all students by default
      setSelectedStudents(new Set(sortedStudents.map(s => s.id)));
    } catch (error: any) {
      console.error('Error loading students:', error);
      alert(`Failed to load students: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const toggleAllStudents = () => {
    if (selectedStudents.size === students.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(students.map(s => s.id)));
    }
  };

  const getNextClass = (currentClass: string): string => {
    const classNum = parseInt(currentClass);
    if (classNum >= 12) {
      return 'Graduated';
    }
    return (classNum + 1).toString();
  };

  const preparePromotion = () => {
    if (selectedStudents.size === 0) {
      alert('Please select at least one student to promote.');
      return;
    }

    const nextClass = getNextClass(selectedClass);
    if (nextClass === 'Graduated') {
      if (!confirm(`${selectedStudents.size} student(s) in class ${selectedClass} will be marked as Graduated. Continue?`)) {
        return;
      }
    }

    // Only include selected students
    const records: PromotionRecord[] = students
      .filter(student => selectedStudents.has(student.id))
      .map(student => ({
        studentId: student.id,
        name: student.name,
        currentClass: student.class,
        currentSection: student.section,
        newClass: nextClass,
        newSection: nextClass === 'Graduated' ? '' : studentSections[student.id] || student.section,
        status: 'pending',
      }));

    setPromotionRecords(records);
    setActiveTab('review');
  };

  const executePromotion = async () => {
    if (!confirm(`Are you sure you want to promote ${promotionRecords.length} students? This action cannot be undone.`)) {
      return;
    }

    setProcessing(true);
    const updatedRecords: PromotionRecord[] = [];

    for (const record of promotionRecords) {
      try {
        // Find the student document
        const oldDocRef = doc(db, `school-users/${domain}/students`, record.studentId);
        const oldDocSnap = await getDoc(oldDocRef);

        if (!oldDocSnap.exists()) {
          updatedRecords.push({
            ...record,
            status: 'failed',
            error: 'Student document not found',
          });
          continue;
        }

        const studentData = oldDocSnap.data();

        // Get enrollment ID (use studentId or rollNumber as enrollment ID)
        const enrollmentId = studentData.enrollmentId || studentData.studentId || studentData.rollNumber;

        // Calculate academic year (e.g., "2024-2025" for promotion happening in 2025)
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth(); // 0-11
        const currentYear = currentDate.getFullYear();

        // If promotion happens between Jan-Mar, it's for the previous academic year
        // If promotion happens between Apr-Dec, it's for the current academic year
        let academicYearStart: number;
        if (currentMonth >= 3) { // April onwards (month 3 = April)
          academicYearStart = currentYear;
        } else { // January to March
          academicYearStart = currentYear - 1;
        }
        const academicYear = `${academicYearStart}-${academicYearStart + 1}`;

        // Archive the old record to school-data archive with enrollment ID as document ID
        const archiveDocRef = doc(db, `school-data/${domain}/archive/${enrollmentId}/academics`, academicYear);
        const archiveData = {
          ...studentData,
          archivedOn: new Date().toISOString(),
          archivedReason: 'promotion',
          academicYear: academicYear,
          previousDocumentId: record.studentId,
          promotedToClass: record.newClass,
          promotedToSection: record.newSection,
        };
        await setDoc(archiveDocRef, archiveData);

        // Use enrollmentId as the document ID (keep it consistent)
        const newDocId = enrollmentId;

        // Update student data
        const updatedData: any = {
          ...studentData,
          class: record.newClass,
          className: record.newClass === 'Graduated' ? 'Graduated' : `Class ${record.newClass}${record.newSection}`,
          section: record.newSection,
          previousClass: record.currentClass,
          previousSection: record.currentSection,
          promotedOn: new Date().toISOString(),
          promotedFrom: record.studentId,
          enrollmentId: enrollmentId, // Ensure enrollmentId is preserved
        };

        // If graduated, add graduation flag
        if (record.newClass === 'Graduated') {
          updatedData.graduated = true;
          updatedData.graduationDate = new Date().toISOString();
        }

        // Create new document with enrollmentId as document ID
        const newDocRef = doc(db, `school-users/${domain}/students`, newDocId);
        await setDoc(newDocRef, updatedData);

        // Update user document with new documentId (which is now just enrollmentId)
        try {
          const userDocRef = doc(db, `school-users/${domain}/users`, enrollmentId);
          await updateDoc(userDocRef, {
            documentId: newDocId,
            updatedOn: new Date().toISOString(),
          });
        } catch (userError) {
          console.error('Error updating user documentId:', userError);
          // Continue with promotion even if user update fails
        }

        // Carry forward unpaid fees from previous year
        try {
          const oldFeesRef = collection(db, `school-users/${domain}/students/${record.studentId}/fees`);
          const feesSnapshot = await getDocs(oldFeesRef);

          if (!feesSnapshot.empty) {
            // Process each fee record
            for (const feeDoc of feesSnapshot.docs) {
              const feeData = feeDoc.data();

              // Only carry forward fees that have outstanding balance
              if (feeData.due && feeData.due > 0 && feeData.status !== 'paid') {
                const newFeeRef = doc(db, `school-users/${domain}/students/${newDocId}/fees`, feeDoc.id);

                // Create carried forward fee record
                const carriedForwardFee = {
                  ...feeData,
                  carriedForward: true,
                  originalYear: academicYear,
                  carriedForwardOn: new Date().toISOString(),
                  previousStudentId: record.studentId,
                };

                await setDoc(newFeeRef, carriedForwardFee);
              }
            }
          }
        } catch (feeError) {
          console.error('Error carrying forward fees:', feeError);
          // Continue with promotion even if fee carryforward fails
        }

        // Archive exam results for this student for the academic year
        try {
          console.log('🔍 Starting exam results archiving for student:', record.studentId);

          // Get all exams from the correct path: school-data/{domain}/exam-results
          const examResultsRef = collection(db, `school-data/${domain}/exam-results`);
          const examsSnapshot = await getDocs(examResultsRef);

          console.log(`📚 Found ${examsSnapshot.docs.length} total exams in database`);

          const resultsData: any[] = [];

          // For each exam, check if this student has results
          for (const examDoc of examsSnapshot.docs) {
            const examData = examDoc.data();
            console.log(`  📝 Checking exam: ${examDoc.id} (${examData.examName || 'No name'})`);

            const studentResultRef = doc(db, `school-data/${domain}/exam-results/${examDoc.id}/student-results`, record.studentId);
            const studentResultSnap = await getDoc(studentResultRef);

            if (studentResultSnap.exists()) {
              const resultData = studentResultSnap.data();
              console.log(`    ✅ Found result for student ${record.studentId} in exam ${examDoc.id}`);

              resultsData.push({
                examId: examDoc.id,
                examData: examData,
                resultId: studentResultSnap.id,
                ...resultData,
                archivedOn: new Date().toISOString(),
                archivedReason: 'promotion',
              });

              // Delete the student result from the exam's subcollection
              await deleteDoc(studentResultRef);
              console.log(`    🗑️ Deleted result from main collection`);
            } else {
              console.log(`    ❌ No result found for student ${record.studentId} in exam ${examDoc.id}`);
            }
          }

          console.log(`📊 Total results found for archiving: ${resultsData.length}`);

          // Archive all results if any were found
          if (resultsData.length > 0) {
            const archiveResultsRef = doc(db, `school-data/${domain}/archive/${enrollmentId}/exam-results`, academicYear);

            await setDoc(archiveResultsRef, {
              academicYear: academicYear,
              enrollmentId: enrollmentId,
              studentName: studentData.name || record.name,
              class: record.currentClass,
              section: record.currentSection,
              results: resultsData,
              totalExams: resultsData.length,
              archivedOn: new Date().toISOString(),
              archivedReason: 'promotion',
            });

            console.log(`✅ Archived ${resultsData.length} exam results to: school-data/${domain}/archive/${enrollmentId}/exam-results/${academicYear}`);
          } else {
            console.log('⚠️ No exam results found to archive for this student');
          }
        } catch (resultsError) {
          console.error('❌ Error archiving exam results:', resultsError);
          // Continue with promotion even if results archiving fails
        }

        // Delete old document
        await deleteDoc(oldDocRef);

        updatedRecords.push({
          ...record,
          status: 'success',
        });
      } catch (error: any) {
        console.error(`Error promoting student ${record.name}:`, error);
        updatedRecords.push({
          ...record,
          status: 'failed',
          error: error.message,
        });
      }
    }

    setPromotionRecords(updatedRecords);
    setProcessing(false);
    setActiveTab('results');
  };

  const updateSectionForStudent = (studentId: string, newSection: string) => {
    setPromotionRecords(prev =>
      prev.map(record =>
        record.studentId === studentId
          ? { ...record, newSection }
          : record
      )
    );
  };

  const updateStudentTargetSection = (studentId: string, section: string) => {
    setStudentSections(prev => ({
      ...prev,
      [studentId]: section
    }));
  };

  const downloadReport = () => {
    const reportData = promotionRecords.map(record => ({
      'Student Name': record.name,
      'Current Class': `${record.currentClass}-${record.currentSection}`,
      'New Class': record.newClass === 'Graduated' ? 'Graduated' : `${record.newClass}-${record.newSection}`,
      'Status': record.status.toUpperCase(),
      'Error': record.error || '',
    }));

    const ws = XLSX.utils.json_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Promotion Report');

    XLSX.writeFile(wb, `student_promotion_class_${selectedClass}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const resetPromotion = () => {
    setPromotionRecords([]);
    setActiveTab('select');
    setSelectedClass('');
    setCurrentSectionFilter('all');
    setTargetSection('');
    setStudents([]);
    setStudentSections({});
    setSelectedStudents(new Set());
  };

  const successCount = promotionRecords.filter(r => r.status === 'success').length;
  const failedCount = promotionRecords.filter(r => r.status === 'failed').length;

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-arrow-up-circle me-2 text-primary-custom"></i>
          Student Promotion
        </h5>
        <small className="text-muted">Promote students to next class with section management</small>
      </div>

      <div className="card-body">
        {/* Tab Navigation */}
        <ul className="nav nav-pills mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'select' ? 'active' : ''}`}
              onClick={() => setActiveTab('select')}
              disabled={processing}
            >
              <i className="bi bi-1-circle me-2"></i>
              Select Students
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'review' ? 'active' : ''}`}
              onClick={() => setActiveTab('review')}
              disabled={promotionRecords.length === 0 || processing}
            >
              <i className="bi bi-2-circle me-2"></i>
              Review & Confirm
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'results' ? 'active' : ''}`}
              disabled={successCount === 0 && failedCount === 0}
            >
              <i className="bi bi-3-circle me-2"></i>
              Results
            </button>
          </li>
        </ul>

        {/* Step 1: Select Students */}
        {activeTab === 'select' && (
          <div>
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Promotion Process:</strong> Select current class and section to filter students. You can adjust target sections individually in the review step.
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <label className="form-label">Current Class *</label>
                <select
                  className="form-select"
                  value={selectedClass}
                  onChange={(e) => {
                    setSelectedClass(e.target.value);
                    setStudents([]);
                  }}
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls} value={cls}>Class {cls}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Current Section</label>
                <select
                  className="form-select"
                  value={currentSectionFilter}
                  onChange={(e) => setCurrentSectionFilter(e.target.value)}
                  disabled={!selectedClass}
                >
                  <option value="all">All Sections</option>
                  {sections.map(sec => (
                    <option key={sec} value={sec}>Section {sec}</option>
                  ))}
                </select>
                <small className="text-muted">
                  Filter students by current section
                </small>
              </div>

              <div className="col-md-3">
                <label className="form-label">Default Target Section</label>
                <select
                  className="form-select"
                  value={targetSection}
                  onChange={(e) => setTargetSection(e.target.value)}
                  disabled={!selectedClass}
                >
                  <option value="">Keep Current Section</option>
                  {sections.map(sec => (
                    <option key={sec} value={sec}>Section {sec}</option>
                  ))}
                </select>
                <small className="text-muted">
                  {targetSection ? `Default: Section ${targetSection}` : 'Default: Keep current'}
                </small>
              </div>

              <div className="col-md-3 d-flex align-items-end">
                <button
                  className="btn btn-primary w-100"
                  onClick={preparePromotion}
                  disabled={!selectedClass || students.length === 0 || loading || selectedStudents.size === 0}
                >
                  <i className="bi bi-arrow-right-circle me-2"></i>
                  Prepare Promotion {selectedStudents.size > 0 && `(${selectedStudents.size})`}
                </button>
              </div>
            </div>

            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading students...</p>
              </div>
            )}

            {!loading && students.length > 0 && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    <i className="bi bi-people me-2"></i>
                    Students in Class {selectedClass} ({students.length} students)
                  </h6>
                  <span className="badge bg-info">
                    {selectedStudents.size} selected
                  </span>
                </div>
                <div className="alert alert-success">
                  <i className="bi bi-check-circle me-2"></i>
                  Selected students will be promoted to <strong>Class {getNextClass(selectedClass)}</strong>
                  {targetSection && ` - Section ${targetSection} (by default)`}
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedStudents.size === students.length && students.length > 0}
                            onChange={toggleAllStudents}
                          />
                        </th>
                        <th>Roll No</th>
                        <th>Name</th>
                        <th>Current Class</th>
                        <th>Section</th>
                        <th>Will Promote To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => (
                        <tr key={student.id} className={selectedStudents.has(student.id) ? 'table-active' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedStudents.has(student.id)}
                              onChange={() => toggleStudentSelection(student.id)}
                            />
                          </td>
                          <td>{student.rollNumber}</td>
                          <td>{student.name}</td>
                          <td>Class {student.class}</td>
                          <td>Section {student.section}</td>
                          <td>
                            {getNextClass(student.class) === 'Graduated' ? (
                              <span className="badge bg-success">Graduated</span>
                            ) : (
                              <div className="d-flex align-items-center gap-2">
                                <span className="badge bg-primary">Class {getNextClass(student.class)}</span>
                                <select
                                  className="form-select form-select-sm"
                                  style={{ width: '100px' }}
                                  value={studentSections[student.id] || student.section}
                                  onChange={(e) => updateStudentTargetSection(student.id, e.target.value)}
                                  disabled={!selectedStudents.has(student.id)}
                                >
                                  {sections.map(sec => (
                                    <option key={sec} value={sec}>Section {sec}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!loading && selectedClass && students.length === 0 && (
              <div className="text-center py-5">
                <i className="bi bi-inbox display-1 text-muted"></i>
                <p className="mt-3 text-muted">No students found in Class {selectedClass}</p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Review & Confirm */}
        {activeTab === 'review' && (
          <div>
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Important:</strong> Review the promotion details below. You can adjust sections for individual students before confirming.
            </div>

            <div className="mb-4">
              <h6>Promotion Summary</h6>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h3 className="mb-1">{promotionRecords.length}</h3>
                      <p className="mb-0 text-muted small">Total Students</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card bg-info bg-opacity-10">
                    <div className="card-body">
                      <h3 className="mb-1 text-info">Class {selectedClass}</h3>
                      <p className="mb-0 text-muted small">Current Class</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card bg-success bg-opacity-10">
                    <div className="card-body">
                      <h3 className="mb-1 text-success">Class {getNextClass(selectedClass)}</h3>
                      <p className="mb-0 text-muted small">Promoted To</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive mb-4">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Student Name</th>
                    <th>Current</th>
                    <th>Promoted To</th>
                    <th>New Section</th>
                  </tr>
                </thead>
                <tbody>
                  {promotionRecords.map((record) => (
                    <tr key={record.studentId}>
                      <td><strong>{record.name}</strong></td>
                      <td>
                        <span className="badge bg-secondary">
                          {record.currentClass}-{record.currentSection}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-primary">
                          {record.newClass === 'Graduated' ? 'Graduated' : record.newClass}
                        </span>
                      </td>
                      <td>
                        {record.newClass !== 'Graduated' && (
                          <select
                            className="form-select form-select-sm"
                            value={record.newSection}
                            onChange={(e) => updateSectionForStudent(record.studentId, e.target.value)}
                            style={{ width: '100px' }}
                          >
                            {sections.map(sec => (
                              <option key={sec} value={sec}>{sec}</option>
                            ))}
                          </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-success btn-lg"
                onClick={executePromotion}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Promoting Students...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Confirm & Execute Promotion
                  </>
                )}
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setActiveTab('select')}
                disabled={processing}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {activeTab === 'results' && (
          <div>
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div className="card bg-success bg-opacity-10 border-success">
                  <div className="card-body text-center">
                    <h2 className="text-success mb-1">{successCount}</h2>
                    <p className="mb-0 text-muted">Successful</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card bg-danger bg-opacity-10 border-danger">
                  <div className="card-body text-center">
                    <h2 className="text-danger mb-1">{failedCount}</h2>
                    <p className="mb-0 text-muted">Failed</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h2 className="mb-1">{promotionRecords.length}</h2>
                    <p className="mb-0 text-muted">Total</p>
                  </div>
                </div>
              </div>
            </div>

            {successCount === promotionRecords.length ? (
              <div className="alert alert-success">
                <i className="bi bi-check-circle me-2"></i>
                <strong>Success!</strong> All students have been promoted successfully.
              </div>
            ) : (
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Partial Success:</strong> Some students could not be promoted. Please review the errors below.
              </div>
            )}

            <div className="table-responsive mb-4">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Student Name</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Status</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {promotionRecords.map((record) => (
                    <tr key={record.studentId} className={record.status === 'failed' ? 'table-danger' : ''}>
                      <td>{record.name}</td>
                      <td>{record.currentClass}-{record.currentSection}</td>
                      <td>
                        {record.newClass === 'Graduated'
                          ? 'Graduated'
                          : `${record.newClass}-${record.newSection}`
                        }
                      </td>
                      <td>
                        {record.status === 'success' ? (
                          <span className="badge bg-success">
                            <i className="bi bi-check-circle me-1"></i>
                            Success
                          </span>
                        ) : (
                          <span className="badge bg-danger">
                            <i className="bi bi-x-circle me-1"></i>
                            Failed
                          </span>
                        )}
                      </td>
                      <td>
                        <small className="text-danger">{record.error || '-'}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-primary"
                onClick={resetPromotion}
              >
                <i className="bi bi-arrow-repeat me-2"></i>
                Promote Another Class
              </button>
              <button
                className="btn btn-outline-success"
                onClick={downloadReport}
              >
                <i className="bi bi-download me-2"></i>
                Download Report
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Archive Info */}
      <div className="card-footer bg-light">
        <small className="text-success d-block">
          <i className="bi bi-check-circle me-2"></i>
          Student records and exam results are automatically archived by academic year during promotion
        </small>
      </div>
    </div>
  );
};

export default StudentPromotion;
